import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ArrowUpIcon } from 'lucide-react';

import { ErrorType, FORM_ERRORS, MIN } from 'constants/formErrors';

import { DetailFormValue } from 'components/DetailsView';
import { DetailList, DetailListRemoveButton } from 'components/DetailsView/DetailList';
import { Button } from 'components/ui/button';
import { TotemLabel } from 'components/TotemLabel';

type ProductSupplierPrice = {
    value: number;
    conditionning: number;
    packagesPerBatch: number;
};

type FormValues = {
    suppliers: {
        prices: ProductSupplierPrice[];
    }[];
};

type PriceError = {
    type: ErrorType;
};

export const PricesArray = ({ supplierIndex, supplierName }: { supplierIndex: number; supplierName: string }) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<FormValues>();
    const {
        fields: priceFields,
        prepend,
        remove,
        move,
    } = useFieldArray<FormValues, `suppliers.${number}.prices`, 'id'>({
        name: `suppliers.${supplierIndex}.prices` as const,
    });

    return (
        <DetailList
            label={`Les prix de ${supplierName}`}
            className="flex-1"
            onAdd={() => prepend({ value: 0, conditionning: 1, packagesPerBatch: 1 })}
            items={priceFields.map((priceField, index) => {
                const priceErrorType = (errors?.suppliers?.[supplierIndex]?.prices?.[index]?.value as PriceError)?.type;
                const conditionningErrorType = (
                    errors?.suppliers?.[supplierIndex]?.prices?.[index]?.conditionning as PriceError
                )?.type;
                const packagesPerBatchErrorType = (
                    errors?.suppliers?.[supplierIndex]?.prices?.[index]?.packagesPerBatch as PriceError
                )?.type;

                const priceNumberValue = priceErrorType === MIN ? '0.01' : '';
                const conditionningNumberValue = conditionningErrorType === MIN ? '1' : '';
                const packagesPerBatchNumberValue = packagesPerBatchErrorType === MIN ? '1' : '';

                return (
                    <React.Fragment key={priceField.id}>
                        <DetailFormValue
                            label={`Prix Nᵒ${index + 1}`}
                            sublabel="Prix HT à l'unité"
                            defaultValue={priceField.value}
                            type="number"
                            step="0.0001"
                            min="0.01"
                            {...register(`suppliers.${supplierIndex}.prices.${index}.value` as const, {
                                min: 0.001,
                                valueAsNumber: true,
                                required: true,
                            })}
                            placeholder="Prix"
                            customError={priceErrorType && FORM_ERRORS[priceErrorType] + priceNumberValue}
                            data-test="supplier-price"
                        />
                        <DetailFormValue
                            label={`Colisage Nᵒ${index + 1}`}
                            sublabel="Nb d'unités vendues dans un colis"
                            placeholder="Colisage"
                            defaultValue={priceField.conditionning || 1}
                            type="number"
                            step="1"
                            min="1"
                            {...register(`suppliers.${supplierIndex}.prices.${index}.conditionning` as const, {
                                min: 1,
                                valueAsNumber: true,
                                required: true,
                            })}
                            customError={
                                conditionningErrorType && FORM_ERRORS[conditionningErrorType] + conditionningNumberValue
                            }
                            data-test="supplier-conditionning"
                        />
                        <DetailFormValue
                            label={`Palettisation Nᵒ${index + 1}`}
                            sublabel="Nb de colis dans une palette"
                            placeholder="Nb de colis"
                            defaultValue={priceField.packagesPerBatch || 1}
                            type="number"
                            step="1"
                            min="1"
                            {...register(`suppliers.${supplierIndex}.prices.${index}.packagesPerBatch` as const, {
                                min: 1,
                                valueAsNumber: true,
                                required: true,
                            })}
                            customError={
                                packagesPerBatchErrorType &&
                                FORM_ERRORS[packagesPerBatchErrorType] + packagesPerBatchNumberValue
                            }
                            data-test="supplier-packages-per-batch"
                        />
                        <div className="flex flex-col gap-1">
                            <Button type="button" size="icon-sm" variant="outline" onClick={() => move(index, 0)}>
                                <ArrowUpIcon />
                            </Button>
                            <DetailListRemoveButton onClick={() => remove(index)} />
                        </div>
                    </React.Fragment>
                );
            })}
        />
    );
};
